// e:\client-loadboard\trucking-loadboard\src\components\AdminBulkTruckPosting.js

import React, { useState, useCallback } from 'react';
import {
    Box, Button, Typography, Paper, Alert, Table, TableBody, TableCell,
    TableContainer, TableHead, TableRow, CircularProgress, Collapse, Card,
    CardContent, Divider, IconButton
} from '@mui/material';
import { styled } from '@mui/material/styles';
import Papa from 'papaparse';
import axios from 'axios';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';

const API_BASE_URL = process.env.REACT_APP_API_BASE || 'http://localhost:5000';
const MAPBOX_ACCESS_TOKEN = process.env.REACT_APP_MAPBOX_ACCESS_TOKEN;

const StyledPaper = styled(Paper)(({ theme }) => ({
    padding: theme.spacing(3),
    marginBottom: theme.spacing(3),
}));

dayjs.extend(utc);
dayjs.extend(timezone);

const getRegistrationType = (registrationNumber) => {
  if (!registrationNumber) return 'DOT/MC';
  const numStr = String(registrationNumber).toUpperCase();
  if (numStr.includes('MC-') || numStr.startsWith('MC')) {
    return 'MC';
  }
  return 'DOT';
};

const DetailSection = ({ title, children }) => (
    <Box sx={{ pr: 2, mb: 2 }}>
      <Typography
        variant="subtitle2"
        sx={{
          fontWeight: "bold",
          fontSize: "0.9rem",
          color: "#11224d",
          mb: 0.5,
          borderBottom: '1px solid #ddd',
          pb: 0.5,
        }}
      >
        {title}
      </Typography>
      {children}
    </Box>
);

const PreviewRow = ({ truck, index }) => {
    const [open, setOpen] = useState(false);

    return (
        <>
            <TableRow hover onClick={() => setOpen(!open)} sx={{ cursor: 'pointer' }}>
                <TableCell>
                    <IconButton size="small">
                        {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                    </IconButton>
                </TableCell>
                <TableCell>{truck.origin}</TableCell>
                <TableCell>{truck.destination}</TableCell>
                <TableCell>{dayjs(truck.availableDate).format('MM/DD/YYYY')}</TableCell>
                <TableCell>{truck.equipment}</TableCell>
                <TableCell>{truck.company_name} ({truck.company_state})</TableCell>
                <TableCell>{truck.fullOrPartial || 'Full'}</TableCell>
                <TableCell>{truck.length ? `${truck.length} ft` : 'N/A'}</TableCell>
            </TableRow>
            <TableRow>
                <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={8}>
                    <Collapse in={open} timeout="auto" unmountOnExit>
                        <Card sx={{ border: "1px solid #f0f0f0", borderRadius: 1, boxShadow: "0 1px 3px rgba(0,0,0,0.05)", backgroundColor: "#ffffff" }}>
                            <CardContent sx={{ p: 1.5 }}>
                                <Typography variant="body2" sx={{ fontWeight: "bold", mb: 1, fontSize: "1rem", color: "#11224d" }}>
                                    {truck.origin} → {truck.destination}
                                </Typography>
                                <Divider sx={{ my: 1 }} />
                                <Box sx={{ display: "flex", width: "100%", mt: 1 }}>
                                    <Box sx={{ flex: '1 1 50%', pr: 2 }}>
                                        <DetailSection title="Truck Details">
                                            <Typography variant="caption" display="block"><b>Equipment:</b> {truck.equipment || "N/A"}</Typography>
                                            <Typography variant="caption" display="block"><b>Load Type:</b> {truck.fullOrPartial || "N/A"}</Typography>
                                            <Typography variant="caption" display="block"><b>Feet:</b> {truck.length ? `${truck.length} ft` : "N/A"}</Typography>
                                            <Typography variant="caption" display="block"><b>Weight:</b> {truck.weight ? `${truck.weight} lbs` : "N/A"}</Typography>
                                            <Typography variant="caption" display="block"><b>Reference ID:</b> {truck.referenceId || "N/A"}</Typography>
                                            <Typography variant="caption" display="block"><b>Notes:</b> {truck.comments || "N/A"}</Typography>
                                        </DetailSection>
                                    </Box>
                                    <Box sx={{ flex: '1 1 50%', pr: 2 }}>
                                        <DetailSection title="Company / Contact">
                                            <Typography variant="caption" display="block"><b>Company:</b> {truck.company_name || "N/A"}</Typography>
                                            <Typography variant="caption" display="block"><b>State:</b> {truck.company_state || "N/A"}</Typography>
                                            <Typography variant="caption" display="block"><b>Contact:</b> {truck.contactInfo || "N/A"}</Typography>
                                            {truck.mc_number && (
                                                <Typography variant="caption" display="block">
                                                    <b>{getRegistrationType(truck.mc_number)}:</b> {truck.mc_number}
                                                </Typography>
                                            )}
                                        </DetailSection>
                                    </Box>
                                </Box>
                            </CardContent>
                        </Card>
                    </Collapse>
                </TableCell>
            </TableRow>
        </>
    );
};

const AdminBulkTruckPosting = () => {
    const [file, setFile] = useState(null);
    const [parsedTrucks, setParsedTrucks] = useState([]);
    const [errors, setErrors] = useState([]);
    const [loading, setLoading] = useState(false);
    const [uploadResponse, setUploadResponse] = useState(null);

    const handleFileChange = (event) => {
        const selectedFile = event.target.files[0];
        // Allow csv, excel, or text types as they often contain CSV data
        if (selectedFile && (selectedFile.type === 'text/csv' || selectedFile.type === 'application/vnd.ms-excel' || selectedFile.name.endsWith('.csv'))) {
            setFile(selectedFile);
            setParsedTrucks([]);
            setErrors([]);
            setUploadResponse(null);
        } else {
            setErrors([{ index: 'File', message: 'Please select a valid CSV file.' }]);
        }
    };

    const validateLocation = async (address) => {
        if (!address || !MAPBOX_ACCESS_TOKEN) return null;
        const endpoint = `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(address)}.json`;
        try {
            const response = await axios.get(endpoint, {
                params: {
                    access_token: MAPBOX_ACCESS_TOKEN,
                    limit: 1,
                    types: 'place,postcode,address',
                    country: 'US,CA,MX',
                    autocomplete: true
                }
            });
            const features = response.data.features;
            if (features.length > 0) {
                const feature = features[0];
                let city = feature.text;
                let state = "";

                if (feature.context) {
                    const region = feature.context.find(c => c.id.startsWith('region'));
                    if (region) {
                        state = region.short_code ? region.short_code.split('-')[1] : region.text;
                    }

                    if (feature.place_type.includes('postcode')) {
                        const place = feature.context.find(c => c.id.startsWith('place'));
                        if (place) city = place.text;
                    }
                }

                return {
                    place_name: (city && state) ? `${city}, ${state}` : feature.place_name,
                    center: feature.center 
                };
            }
        } catch (error) {
            console.error("Error validating address:", address, error);
        }
        return null;
    };

  const handleParse = useCallback(async () => {
    if (!file) return;

    setLoading(true);
    setErrors([]);

    Papa.parse(file, {
        header: true,
        skipEmptyLines: true,
        transformHeader: (header) => header.trim().toLowerCase(), // Normalize headers here
        complete: async (results) => {
            const validationErrors = [];
            const processedTrucks = [];

            // 1. Flexible mappings for CSV headers (all lowercase)
            const fieldMappings = {
                origin: ['origin', 'pickup', 'location', 'origin city', 'city', 'starting city', 'source'],
                destination: ['destination', 'dropoff', 'dest', 'destination city', 'end city'],
                availableDate: ['availabledate', 'available date', 'date', 'available', 'pickup date', 'start date'],
                equipment: ['equipment', 'truck type', 'truck', 'type', 'trailer'],
                company_name: ['company_name', 'company name', 'company', 'carrier', 'carrier name'],
                state: ['state', 'company state', 'base state', 'home state', 'carrier state', 'province', 'st'],
                mc_number: ['mc_number', 'mc', 'mc number', 'dot', 'dot number', 'dot_number', 'registration number'],
                loadType: ['loadtype', 'load type', 'load', 'full or partial', 'type of load', 'full/partial'],
                length: ['length', 'feet', 'ft', 'size'],
                weight: ['weight', 'lbs', 'capacity'],
                contactInfo: ['contactinfo', 'contact', 'contact info', 'phone', 'email', 'contact number'],
                comments: ['comments', 'notes', 'remarks', 'description', 'details'],
                referenceId: ['referenceid', 'reference id', 'ref id', 'ref', 'reference']
            };

            for (const [index, row] of results.data.entries()) {
                if (Object.keys(row).length === 0) continue;

                const getValue = (fieldKey) => {
                    const possibleKeys = fieldMappings[fieldKey] || [];
                    for (const key of possibleKeys) {
                        if (row[key] !== undefined && row[key] !== null && row[key] !== '') {
                            return String(row[key]).trim();
                        }
                    }
                    return null;
                };

                // 3. REQUIRED FIELDS CHECK & DATE PROCESSING
                const requiredFields = ['origin', 'availableDate', 'equipment', 'company_name', 'state'];
                const truck = {};
                let hasError = false;

                for (const field of requiredFields) {
                    let val = getValue(field);
                    
                    // Standardize Date for the Backend
                    if (field === 'availableDate' && val) {
                        const d = dayjs(val);
                        if (d.isValid()) {
                            val = d.toISOString(); 
                        } else {
                            validationErrors.push({ index, message: `Row ${index + 1}: Invalid date format '${val}'` });
                            hasError = true;
                        }
                    }

                    if (!val) {
                        validationErrors.push({ index, message: `Row ${index + 1}: Missing required field '${field}'. Check your CSV headers.` });
                        hasError = true;
                    } else {
                        truck[field] = val;
                    }
                }

                if (hasError) continue;

                // 4. ROLE & STATE DEFAULTS
                truck['posted_by_role'] = 'carrier';
                truck['company_state'] = truck['state'];

                // 5. OPTIONAL FIELDS
                const optionalFields = ['destination', 'length', 'weight', 'loadType', 'contactInfo', 'comments', 'referenceId', 'mc_number'];
                optionalFields.forEach(field => {
                    truck[field] = getValue(field);
                });

                // 6. LOAD TYPE MAPPING
                if (truck.loadType) {
                    const lt = truck.loadType.toLowerCase();
                    if (lt.includes('full') && lt.includes('partial')) {
                        truck.fullOrPartial = 'Full or Partial';
                    } else if (lt === 'full' || lt === 'f') {
                        truck.fullOrPartial = 'Full';
                    } else if (lt === 'partial' || lt === 'p') {
                        truck.fullOrPartial = 'Partial';
                    } else if (lt === 'both') {
                        truck.fullOrPartial = 'Full or Partial';
                    } else {
                        truck.fullOrPartial = 'Full';
                    }
                } else {
                    truck.fullOrPartial = 'Full';
                }

                // 7. GEOCODING ENRICHMENT
                try {
                    const [originData, destData] = await Promise.all([
                        validateLocation(truck.origin),
                        validateLocation(truck.destination)
                    ]);

                    if (originData) {
                        truck.origin = originData.place_name;
                        truck.originCoords = originData.center;
                    }
                    if (destData) {
                        truck.destination = destData.place_name;
                        truck.destinationCoords = destData.center;
                    }
                } catch (e) {
                    console.error("Geocoding error for row:", index, e);
                }

                truck.timePosted = new Date().toISOString(); 
                processedTrucks.push(truck);
            }

            if (validationErrors.length > 0) {
                setErrors(validationErrors);
            }
            setParsedTrucks(processedTrucks);
            setLoading(false);
        }
    });
}, [file, validateLocation]);

    const handlePostTrucks = async () => {
        if (parsedTrucks.length === 0) {
            setErrors([{ index: 'Validation', message: 'No valid trucks to post.' }]);
            return;
        }

        setLoading(true);
        setUploadResponse(null);
        setErrors([]);

        try {
            const token = localStorage.getItem('jwtToken');
            const response = await axios.post(`${API_BASE_URL}/api/admin/trucks/bulk`, { trucks: parsedTrucks }, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setUploadResponse(response.data);
            setParsedTrucks([]); 
        } catch (err) {
            const errorMessage = err.response?.data?.message || 'An error occurred during bulk posting.';
            setErrors([{ index: 'API', message: errorMessage }]);
        } finally {
            setLoading(false);
        }
    };

    return (
        <Box sx={{ p: 2 }}>
            <StyledPaper>
                <Typography variant="h6" gutterBottom>Bulk Post Trucks via CSV</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Upload a CSV file with truck data. <br/>
                    <b>Required Headers:</b> origin, availableDate, equipment, company_name, state. <br/>
                    <b>Optional Headers:</b> destination, length, weight, loadType (Full/Partial), contactInfo, comments, referenceId, mc_number (or dot_number).
                </Typography>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                    <Button variant="contained" component="label">
                        Choose CSV File
                        <input type="file" hidden accept=".csv" onChange={handleFileChange} />
                    </Button>
                    {file && <Typography variant="body2">{file.name}</Typography>}
                    <Button onClick={handleParse} disabled={!file || loading} variant="outlined">
                        Preview Trucks
                    </Button>
                </Box>
            </StyledPaper>

            {loading && <CircularProgress sx={{ display: 'block', margin: '2rem auto' }} />}

            {errors.length > 0 && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    <Typography fontWeight="bold">Validation Errors:</Typography>
                    {errors.map((e, i) => <Typography key={i} variant="body2">- {e.message}</Typography>)}
                </Alert>
            )}

            {uploadResponse && (
                <Alert severity={uploadResponse.errors?.length > 0 ? "warning" : "success"} sx={{ mb: 2 }}>
                    {uploadResponse.message}
                    {uploadResponse.errors?.length > 0 && (
                        <Box sx={{ mt: 1 }}>
                            <Typography fontWeight="bold">Details:</Typography>
                            {uploadResponse.errors.map((e, i) => (
                                <Typography key={i} variant="body2">- Row {e.index + 1}: {e.message}</Typography>
                            ))}
                        </Box>
                    )}
                </Alert>
            )}

            {parsedTrucks.length > 0 && !loading && (
                <>
                    <Typography variant="h6" gutterBottom>Truck Preview</Typography>
                    <TableContainer component={Paper}>
                        <Table size="small">
                            <TableHead sx={{ backgroundColor: '#11224d' }}>
                                <TableRow>
                                    <TableCell />
                                    <TableCell sx={{ color: 'white' }}>Origin</TableCell>
                                    <TableCell sx={{ color: 'white' }}>Destination</TableCell>
                                    <TableCell sx={{ color: 'white' }}>Available</TableCell>
                                    <TableCell sx={{ color: 'white' }}>Equipment</TableCell>
                                    <TableCell sx={{ color: 'white' }}>Company</TableCell>
                                    <TableCell sx={{ color: 'white' }}>Type</TableCell>
                                    <TableCell sx={{ color: 'white' }}>Length</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {parsedTrucks.map((truck, index) => (
                                    <PreviewRow key={index} truck={truck} index={index} />
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
                        <Button variant="contained" color="primary" onClick={handlePostTrucks} disabled={loading}>
                            Confirm and Post {parsedTrucks.length} Trucks
                        </Button>
                    </Box>
                </>
            )}
        </Box>
    );
};

export default AdminBulkTruckPosting;
